// src/engine/branching.ts

import { Question, UserAnswer, SkippedQuestion } from '../types';

export function getVisibleQuestions(
  allQuestions: Question[],
  answers: UserAnswer[]
): Question[] {
  const { visible } = getVisibleQuestionsAndSkipped(allQuestions, answers);
  return visible;
}

export function getVisibleQuestionsAndSkipped(
  allQuestions: Question[],
  answers: UserAnswer[]
): {
  visible: Question[];
  skipped: SkippedQuestion[];
} {
  const answerMap = new Map(answers.map(a => [a.questionId, a.value]));
  const visible: Question[] = [];
  const skipped: SkippedQuestion[] = [];

  allQuestions.forEach((question) => {
    // Always show if no conditional logic
    if (!question.conditionalOn) {
      visible.push(question);
      return;
    }

    // Check if condition is met
    const { questionId, requiredValue } = question.conditionalOn;
    const userAnswer = answerMap.get(questionId);

    if (!userAnswer) {
      // Prerequisite not answered yet - don't mark as skipped
      return;
    }

    // Check if condition is met
    let conditionMet = false;

    if (Array.isArray(requiredValue)) {
      if (Array.isArray(userAnswer)) {
        conditionMet = userAnswer.some(val => requiredValue.includes(val));
      } else {
        conditionMet = requiredValue.includes(userAnswer as string);
      }
    } else {
      if (Array.isArray(userAnswer)) {
        conditionMet = userAnswer.includes(requiredValue as string);
      } else {
        conditionMet = userAnswer === requiredValue;
      }
    }

    if (conditionMet) {
      visible.push(question);
    } else {
      // Question skipped due to branching
      const triggerQuestion = allQuestions.find(q => q.id === questionId);
      
      const skipReason = getSkipReason(
        question.section,
        questionId,
        Array.isArray(userAnswer) ? userAnswer.join(', ') : (userAnswer as string)
      );

      skipped.push({
        id: question.id,
        question: question.question,
        section: question.section,
        reason: skipReason,
        triggerQuestion: triggerQuestion?.question || 'Previous answer',
        triggerAnswer: Array.isArray(userAnswer) ? userAnswer.join(', ') : (userAnswer as string),
      });
    }
  });

  return { visible, skipped };
}

function getSkipReason(
  section: string,
  triggerQuestionId: string,
  triggerAnswer: string
): string {
  // Map of common skip reasons
  const skipReasons: Record<string, string> = {
    'physical-Q3-no': 'Skipped because you indicated no physical facility',
    'physical-Q3-cloud': 'Skipped because you operate fully digitally',
    'ai-Q4': 'Skipped because you don\'t use AI/ML systems',
    'ba-Q1': 'Skipped based on your organization type',
  };

  const key = `${section}-${triggerQuestionId}-${triggerAnswer}`;
  if (skipReasons[key]) {
    return skipReasons[key];
  }

  const sectionKey = `${section}-${triggerQuestionId}`;
  if (skipReasons[sectionKey]) {
    return skipReasons[sectionKey];
  }

  // Fallback
  return `Skipped based on your previous answer (${triggerAnswer})`;
}

export function getNextQuestion(
  visibleQuestions: Question[],
  answeredQuestionIds: string[]
): Question | null {
  return visibleQuestions.find(q => !answeredQuestionIds.includes(q.id)) || null;
}

export function getProgressPercentage(
  currentIndex: number,
  totalQuestions: number
): number {
  if (totalQuestions === 0) return 0;
  return Math.round((currentIndex / totalQuestions) * 100);
}

// Example branching scenarios
export const branchingExamples = {
  // If user has physical facility (Q3 = yes/hybrid) → Show Q21-Q25
  physical_safeguards: {
    trigger: 'Q3',
    values: ['yes', 'hybrid'],
    shows: ['Q21', 'Q22', 'Q23', 'Q24', 'Q25'],
  },

  // If user uses AI (Q4 includes 'ai') → Show Q10, Q41-Q47, Q50
  ai_questions: {
    trigger: 'Q4',
    values: ['ai'],
    shows: ['Q10', 'Q41', 'Q42', 'Q43', 'Q44', 'Q45', 'Q46', 'Q47', 'Q50'],
  },

  // If user is covered entity (Q1 = provider/plan/clearinghouse) → Show Q20
  covered_entity_ba_questions: {
    trigger: 'Q1',
    values: ['provider', 'plan', 'clearinghouse'],
    shows: ['Q20'],
  },

  // If user uses cloud (Q4 includes 'cloud') → Show Q40
  cloud_ba_question: {
    trigger: 'Q4',
    values: ['cloud'],
    shows: ['Q40'],
  },

  // If user had breach (Q33 = yes_reported/yes_unreported) → Show Q36
  breach_notification_question: {
    trigger: 'Q33',
    values: ['yes_reported', 'yes_unreported'],
    shows: ['Q36'],
  },

  // If user operates in multiple states AND uses AI (Q4 includes both) → Show Q50
  state_ai_laws: {
    trigger: 'Q4',
    values: ['ai', 'multistate'],
    shows: ['Q50'],
  },
};
